import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { TopicTrend } from "@/api/stats-types";
import { prettySlug } from "./format";

// Self-contained multi-line SVG chart of per-topic ability over time (no chart
// dependency — matches the embedded-SVG style used by the world map). Plots
// mastery (0–100%); falls back to the topic's theta percentile when mastery is
// absent. Both share a 0–100 scale, so they overlay cleanly.
const COLORS = ["#635bf0", "#ff9f1c", "#10b981", "#ef4444", "#8b5cf6", "#0ea5e9"];

const W = 640;
const H = 220;
const PAD = { l: 34, r: 12, t: 12, b: 28 };

interface Line {
  slug: string;
  color: string;
  pts: { t: number; v: number }[];
}

function valueOf(p: { mastery: number | null; theta_percentile: number | null }): number | null {
  if (p.mastery != null) return p.mastery * 100;
  if (p.theta_percentile != null) return p.theta_percentile;
  return null;
}

export function TrendChart({ topics }: { topics: TopicTrend[] }) {
  const { t } = useTranslation();
  const { lines, minT, maxT } = useMemo(() => {
    const candidates = [...topics]
      .map((tt) => {
        const pts = tt.points
          .map((p) => ({ t: new Date(p.date).getTime(), v: valueOf(p) }))
          .filter((p): p is { t: number; v: number } => p.v != null && !Number.isNaN(p.t))
          .sort((a, b) => a.t - b.t);
        return { slug: tt.topic_slug, pts };
      })
      .filter((l) => l.pts.length >= 2)
      .sort((a, b) => b.pts.length - a.pts.length)
      .slice(0, 6);

    const allT = candidates.flatMap((l) => l.pts.map((p) => p.t));
    const built: Line[] = candidates.map((l, i) => ({
      slug: l.slug,
      color: COLORS[i % COLORS.length],
      pts: l.pts,
    }));
    return {
      lines: built,
      minT: allT.length ? Math.min(...allT) : 0,
      maxT: allT.length ? Math.max(...allT) : 1,
    };
  }, [topics]);

  if (lines.length === 0) {
    return (
      <p className="rounded-xl bg-black/[0.02] px-4 py-6 text-center text-sm text-ink-faint">
        {t("Not enough history yet — keep practising and your progress will chart here.")}
      </p>
    );
  }

  const span = Math.max(1, maxT - minT);
  const x = (t: number) => PAD.l + ((t - minT) / span) * (W - PAD.l - PAD.r);
  const y = (v: number) => H - PAD.b - (Math.max(0, Math.min(100, v)) / 100) * (H - PAD.t - PAD.b);

  const dateLabel = (t: number) =>
    new Date(t).toLocaleDateString(undefined, { month: "short", day: "numeric" });

  return (
    <div className="card p-4">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-label={t("Ability over time")}>
        {/* Y gridlines */}
        {[0, 50, 100].map((g) => (
          <g key={g}>
            <line x1={PAD.l} y1={y(g)} x2={W - PAD.r} y2={y(g)} stroke="currentColor" className="text-black/10" strokeWidth={1} />
            <text x={PAD.l - 6} y={y(g) + 3} textAnchor="end" className="fill-ink-faint" fontSize={10}>
              {g}
            </text>
          </g>
        ))}
        {/* X end labels */}
        <text x={PAD.l} y={H - 8} textAnchor="start" className="fill-ink-faint" fontSize={10}>
          {dateLabel(minT)}
        </text>
        <text x={W - PAD.r} y={H - 8} textAnchor="end" className="fill-ink-faint" fontSize={10}>
          {dateLabel(maxT)}
        </text>
        {/* Lines */}
        {lines.map((l) => (
          <g key={l.slug}>
            <polyline
              fill="none"
              stroke={l.color}
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
              points={l.pts.map((p) => `${x(p.t)},${y(p.v)}`).join(" ")}
            />
            {l.pts.map((p, i) => (
              <circle key={i} cx={x(p.t)} cy={y(p.v)} r={2.5} fill={l.color} />
            ))}
          </g>
        ))}
      </svg>
      {/* Legend */}
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
        {lines.map((l) => (
          <span key={l.slug} className="inline-flex items-center gap-1.5 text-xs text-ink-faint">
            <span className="h-2 w-2 rounded-full" style={{ background: l.color }} />
            {prettySlug(l.slug)}
          </span>
        ))}
      </div>
    </div>
  );
}
